import { forwardRef, useRef } from 'react';
import { combineClassNames } from '../../../Utils/ClassnameUtils';

export type GWPFormTabOption<T extends string> = {
	value: T;
	label: React.ReactNode;
	badge?: React.ReactNode;
	badgeLabel?: string;
	disabled?: boolean;
};

export interface GWPFormTabsProps<T extends string> extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange'> {
	id: string;
	options: ReadonlyArray<GWPFormTabOption<T>>;
	value: T;
	onValueChange: (value: T) => void;
	disabled?: boolean;
}

type GWPFormTabsComponent = <T extends string>(
	props: GWPFormTabsProps<T> & React.RefAttributes<HTMLDivElement>,
) => React.ReactElement | null;

const GWPFormTabsInner = forwardRef(function GWPFormTabsInner<T extends string>(
	props: GWPFormTabsProps<T>,
	ref: React.ForwardedRef<HTMLDivElement>,
) {
	const {
		id,
		options,
		value,
		onValueChange,
		disabled = false,
		className,
		'aria-label': ariaLabel,
		'aria-labelledby': ariaLabelledBy,
		...rest
	} = props;
	const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

	const optionIsEnabled = (index: number) => Boolean(options[index]) && !disabled && !options[index]?.disabled;

	const focusAndSelect = (index: number) => {
		const option = options[index];
		if (!option || !optionIsEnabled(index)) return;
		onValueChange(option.value);
		buttonRefs.current[index]?.focus();
	};

	const moveSelection = (currentIndex: number, direction: 1 | -1) => {
		let nextIndex = currentIndex;
		for (let attempts = 0; attempts < options.length; attempts += 1) {
			nextIndex = (nextIndex + direction + options.length) % options.length;
			if (optionIsEnabled(nextIndex)) {
				focusAndSelect(nextIndex);
				return;
			}
		}
	};

	const firstEnabledIndex = options.findIndex((_, index) => optionIsEnabled(index));
	const lastEnabledIndex = options.reduce((lastIndex, _, index) => (optionIsEnabled(index) ? index : lastIndex), -1);
	const selectedIndex = options.findIndex((option) => option.value === value);
	const tabStopIndex = optionIsEnabled(selectedIndex) ? selectedIndex : firstEnabledIndex;

	return (
		<div ref={ref} className={combineClassNames('gwp-form-tabs', className)} {...rest}>
			<div
				className="gwp-form-tabs-list"
				role="tablist"
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledBy}
				aria-orientation="horizontal"
			>
				{options.map((option, index) => {
					const selected = option.value === value;
					const optionDisabled = disabled || option.disabled;

					return (
						<button
							key={option.value}
							ref={(node) => {
								buttonRefs.current[index] = node;
							}}
							type="button"
							role="tab"
							id={`${id}-tab-${option.value}`}
							aria-controls={`${id}-panel-${option.value}`}
							aria-selected={selected}
							tabIndex={index === tabStopIndex ? 0 : -1}
							disabled={optionDisabled}
							className={combineClassNames('gwp-form-tab', selected ? 'selected' : undefined)}
							onClick={() => onValueChange(option.value)}
							onKeyDown={(event) => {
								switch (event.key) {
									case 'ArrowRight':
										event.preventDefault();
										moveSelection(index, 1);
										break;
									case 'ArrowLeft':
										event.preventDefault();
										moveSelection(index, -1);
										break;
									case 'Home':
										event.preventDefault();
										focusAndSelect(firstEnabledIndex);
										break;
									case 'End':
										event.preventDefault();
										focusAndSelect(lastEnabledIndex);
										break;
								}
							}}
						>
							<span>{option.label}</span>
							{option.badge !== undefined ? (
								<strong className="gwp-form-tab-badge" aria-label={option.badgeLabel}>
									{option.badge}
								</strong>
							) : null}
						</button>
					);
				})}
			</div>
		</div>
	);
});

GWPFormTabsInner.displayName = 'GWPFormTabs';

const GWPFormTabs = GWPFormTabsInner as GWPFormTabsComponent;

export default GWPFormTabs;
